import React, { useState, useRef, useEffect } from "react";
import { LoadScriptNext, Autocomplete } from "@react-google-maps/api";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DashboardLayout } from "../dashboard/DashboardLayout";
import { createSalesman } from "../../redux/actions/salesmanAction";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import { FullScreenLoader } from "../Components/Loader";
import { FiEye, FiEyeOff, FiMapPin, FiUser, FiPhone, FiMail, FiLock, FiMap, FiAlertCircle } from "react-icons/fi";
const libraries = ["places"];

const CreateSalesman = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.salesman || {});

  // Get salesman data from location state if in edit mode
  const salesmanData = location.state?.salesmanData;
  const editMode = location.state?.editMode || false;

  const [showPassword, setShowPassword] = useState(false);
  const [isAddressFetched, setIsAddressFetched] = useState(false);
  const [disableCity, setDisableCity] = useState(false);
  const [disableState, setDisableState] = useState(false);
  const [disablePincode, setDisablePincode] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const autocompleteRef = useRef(null);
  const [fieldErrors, setFieldErrors] = useState({
    name: "",
    password: "",
  });
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    password: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    user_id: "",
  });

  // Pre-fill form data when in edit mode
  useEffect(() => {
    if (editMode && salesmanData) {
      setFormData({
        name: salesmanData.name || "",
        mobile: salesmanData.mobile || "",
        email: salesmanData.email || "",
        password: "", // Don't pre-fill password
        address: salesmanData.address || "",
        city: salesmanData.city || "",
        state: salesmanData.state || "",
        pincode: salesmanData.pincode || "",
        user_id: salesmanData.user_id || "",
      });

      // If address fields are already filled, disable them
      if (salesmanData.city) {
        setDisableCity(true);
      }
      if (salesmanData.state) {
        setDisableState(true);
      }
      if (salesmanData.pincode) {
        setDisablePincode(true);
      }
      if (salesmanData.address) {
        setIsAddressFetched(true);
      }
    }
  }, [editMode, salesmanData]);

  const handlePlaceChanged = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current?.getPlace();

    if (place) {
      let address = "";
      let city = "";
      let state = "";
      let pincode = "";

      // Extract the formatted address, city, state, pincode from the place object
      place.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes("locality")) {
          city = component.long_name;
        }
        if (types.includes("administrative_area_level_1")) {
          state = component.long_name;
        }
        if (types.includes("postal_code")) {
          pincode = component.long_name;
        }
      });

      address = place.formatted_address;

      // Update the formData state with the extracted values
      setFormData((prev) => ({
        ...prev,
        address,
        city,
        state,
        pincode,
      }));
      setDisableCity(city !== "");
      setDisableState(state !== "");
      setDisablePincode(pincode !== "");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") {


      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      // Validate on change
      const error = validateName(value);
      setFieldErrors((prev) => ({
        ...prev,
        name: error,
      }));
    } else if (name === "password") {

      if (/\s/.test(value)) {
        return; // Don't allow any whitespace
      }
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      // Validate on change
      const error = validatePassword(value);
      setFieldErrors((prev) => ({
        ...prev,
        password: error,
      }));
    }
    else if (name === "mobile") {
      const cleanedValue = value.replace(/\D/g, "");
      if (/^[6-9]\d{0,9}$/.test(cleanedValue) || cleanedValue === "") {
        setFormData((prev) => ({
          ...prev,
          [name]: cleanedValue,
        }));
      }
    } else if (name === "pincode") {
      const cleanedValue = value.replace(/\D/g, "");
      if (cleanedValue.length <= 6) {
        setFormData((prev) => ({
          ...prev,
          [name]: cleanedValue,
        }));
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();


    const trimmedName = formData.name.trim();
    const trimmedPassword = formData.password.trim();

    // Basic validation
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }


    // Validate name
    const nameError = validateName(trimmedName);
    if (nameError) {
      setFieldErrors((prev) => ({ ...prev, name: nameError }));
      toast.error(nameError);
      return;
    }

    // Validate password
    const passwordError = validatePassword(trimmedPassword);
    if (passwordError) {
      setFieldErrors((prev) => ({ ...prev, password: passwordError }));
      toast.error(passwordError);
      return;
    }
    // Simple email format validation (if email is provided)
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error("Please enter a valid email address");
        return;
      }
    }

    if (formData.mobile.length !== 10) {
      toast.error("Mobile number must be 10 digits");
      return;
    }

    if (formData.pincode && formData.pincode.length !== 6) {
      toast.error("Pincode must be 6 digits");
      return;
    }

    // Password validation - required for create, optional for update
    if (!editMode && !formData.password) {
      toast.error("Password is required");
      return;
    }

    if (!editMode && formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      // Prepare data for API
      const submitData = {
        ...formData,
        name: trimmedName,
        password: trimmedPassword,
      };
      // For update mode, include user_id
      if (editMode) {
        submitData.user_id = formData.user_id;
        // Remove password if empty (don't update password)
        if (!submitData.password || submitData.password === "") {
          delete submitData.password;
        }
      } else {
        // For create mode, remove user_id
        delete submitData.user_id;
      }

      const result = await dispatch(
        createSalesman(submitData, userInfo.access_token)
      );

      if (result.type === "CREATE_SALESMAN_SUCCESS") {
        toast.success(editMode ? "Salesman updated successfully!" : "Salesman created successfully!");
        navigate("/dashboard/salesmanlist");
      } else {
        // Handle error response
        const errorPayload = result.payload;
        let errorMessage = editMode ? "Failed to update salesman" : "Failed to create salesman";

        // Check if error response has message property
        if (errorPayload?.message) {
          errorMessage = errorPayload.message;
        } else if (typeof errorPayload === "string") {
          errorMessage = errorPayload;
        }

        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error(editMode ? "Failed to update salesman. Please try again." : "Failed to create salesman. Please try again.");
    }
  };

  const validateName = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return "Name is required";
    }
    if (trimmedValue.length < 2) {
      return "Name must be at least 2 characters";
    }
    if (!/^[a-zA-Z\s'-]*$/.test(trimmedValue)) {
      return "Name can only contain letters, spaces, hyphens, and apostrophes";
    }
    return "";
  };

  const validatePassword = (value) => {
    // Check if password contains only spaces
    if (/^\s+$/.test(value)) {
      return "Password cannot contain only spaces";
    }

    if (!editMode && !value) {
      return "Password is required";
    }

    if (editMode && value && value.length > 0) {
      if (value.length < 6) {
        return "Password must be at least 6 characters";
      }
    }

    if (!editMode && value.length > 0) {
      if (value.length < 6) {
        return "Password must be at least 6 characters";
      }
    }
    return "";
  };

  return (
    <DashboardLayout>
      <FullScreenLoader
        show={loading}
        message={loading ? (editMode ? "Updating salesman..." : "Creating salesman...") : ""}
        variant="primary"
        blurIntensity="md"
      />
      <LoadScriptNext
        googleMapsApiKey="AIzaSyBjwQjSQoYFe5OzJxbhnodl-usXAt6AH8g"
        libraries={libraries}
        onLoad={() => setMapReady(true)}
      >
        {mapReady ? (
          <div className="relative pt-4 px-4">
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => navigate(-1)}
                className="hover:text-gray-800 transition-colors p-2 hover:bg-amber-50 rounded-lg"
              >
                <FaArrowLeft size={20} />
              </button>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-amber-500 bg-clip-text text-transparent">
                {editMode ? "Edit Salesman" : "Create Salesman"}
              </h2>
            </div>

            <div className="flex justify-center items-start relative z-10 pb-8">
              {/* Form Container */}
              <div className="w-full max-w-4xl">
                <form
                  onSubmit={handleSubmit}
                  className="relative z-10 bg-white border-2 border-amber-500 rounded-2xl p-6 shadow-2xl space-y-6"
                >
                  {/* Step 1: Personal Information */}
                  <div className="bg-gradient-to-br from-amber-50/30 to-white border-2 border-amber-200 rounded-xl p-5">
                    <h3 className="text-lg font-semibold text-gray-950 mb-4 flex items-center gap-2">
                      <span className="text-xl">👤</span>
                      Step 1: Personal Information
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Name */}
                      {/* <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition"
                            placeholder="Enter salesman name"
                            required
                          />
                        </div>
                      </div> */}
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`w-full pl-10 pr-4 py-2.5 border-2 rounded-lg focus:outline-none transition ${fieldErrors.name
                                ? "border-red-500 focus:border-red-500 bg-red-50"
                                : "border-gray-300 focus:border-amber-500"
                              }`}
                            placeholder="Enter salesman name"
                            required
                          />
                        </div>
                        {fieldErrors.name && (
                          <div className="mt-1.5 flex items-center gap-1 text-red-600 text-xs">
                            <FiAlertCircle size={14} />
                            <span>{fieldErrors.name}</span>
                          </div>
                        )}
                      </div>


                      {/* Mobile */}
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Mobile <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="tel"
                            id="mobile"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleChange}
                            className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition"
                            placeholder="Enter 10-digit mobile number"
                            maxLength="10"
                            required
                          />
                        </div>
                      </div>

                      {/* Email */}
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email
                        </label>
                        <div className="relative">
                          <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition"
                            placeholder="Enter email address"
                          />
                        </div>
                      </div>

                      {/* Password */}
                      {/* <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Password {!editMode && <span className="text-red-500">*</span>}
                          {editMode && <span className="text-gray-500 text-xs">(Leave blank to keep current password)</span>}
                        </label>
                        <div className="relative">
                          <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className="w-full pl-10 pr-12 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition"
                            placeholder={editMode ? "Enter new password (optional)" : "Enter password (min 6 characters)"}
                            minLength={editMode ? undefined : "6"}
                            required={!editMode}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-amber-600 hover:text-amber-700"
                          >
                            {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                          </button>
                        </div>
                      </div> */}

                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Password {!editMode && <span className="text-red-500">*</span>}
                          {editMode && <span className="text-gray-500 text-xs">(Leave blank to keep current password)</span>}
                        </label>
                        <div className="relative">
                          <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className={`w-full pl-10 pr-12 py-2.5 border-2 rounded-lg focus:outline-none transition ${fieldErrors.password
                                ? "border-red-500 focus:border-red-500 bg-red-50"
                                : "border-gray-300 focus:border-amber-500"
                              }`}
                            placeholder={editMode ? "Enter new password (optional)" : "Enter password (min 6 characters)"}
                            minLength={editMode ? undefined : "6"}
                            required={!editMode}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-amber-600 hover:text-amber-700"
                          >
                            {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                          </button>
                        </div>
                        {fieldErrors.password && (
                          <div className="mt-1.5 flex items-center gap-1 text-red-600 text-xs">
                            <FiAlertCircle size={14} />
                            <span>{fieldErrors.password}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Address Information */}
                  <div className="bg-gradient-to-br from-blue-50/30 to-white border-2 border-blue-200 rounded-xl p-5">
                    <h3 className="text-lg font-semibold text-gray-950 mb-4 flex items-center gap-2">
                      <span className="text-xl">📍</span>
                      Step 2: Address Information
                    </h3>

                    <div className="space-y-4">
                      {/* Address Autocomplete */}
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Address <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Autocomplete
                            onLoad={(ref) => (autocompleteRef.current = ref)}
                            onPlaceChanged={() => {
                              handlePlaceChanged();
                              setIsAddressFetched(true);
                            }}
                          >
                            <input
                              type="text"
                              name="address"
                              value={formData.address}
                              onChange={(e) => {
                                handleChange(e);
                                setIsAddressFetched(false);
                              }}
                              placeholder="Start typing address..."
                              className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition"
                              required
                            />
                          </Autocomplete>
                          <FiMapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-600 w-5 h-5" />
                        </div>
                        {isAddressFetched && (
                          <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                            <span>✓</span> Address details auto-filled
                          </p>
                        )}
                      </div>

                      {/* City, State, Pincode */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* City */}
                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            City <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <FiMap className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                              type="text"
                              name="city"
                              value={formData.city}
                              onChange={handleChange}
                              disabled={disableCity}
                              placeholder="City"
                              className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition disabled:bg-gray-100 disabled:cursor-not-allowed"
                              required
                            />
                          </div>
                        </div>

                        {/* State */}
                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            State <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <FiMap className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                              type="text"
                              name="state"
                              value={formData.state}
                              onChange={handleChange}
                              disabled={disableState}
                              placeholder="State"
                              className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition disabled:bg-gray-100 disabled:cursor-not-allowed"
                              required
                            />
                          </div>
                        </div>

                        {/* Pincode */}
                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Pincode <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <FiMap className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                              type="text"
                              name="pincode"
                              value={formData.pincode}
                              onChange={handleChange}
                              disabled={disablePincode}
                              placeholder="6-digit pincode"
                              maxLength="6"
                              className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition disabled:bg-gray-100 disabled:cursor-not-allowed"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="bg-white border-2 border-red-500 text-red-600 py-2.5 px-6 rounded-lg hover:bg-red-50 font-semibold transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-gradient-to-r from-amber-500 to-amber-600 text-white py-2.5 px-6 rounded-lg hover:from-amber-600 hover:to-amber-700 font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {loading ? (editMode ? "Updating..." : "Creating...") : (editMode ? "Update Salesman" : "Create Salesman")}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center min-h-screen">
            <p className="text-gray-500 text-lg">Loading Google Maps...</p>
          </div>
        )}
      </LoadScriptNext>
    </DashboardLayout>
  );
};

export default CreateSalesman;
